import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { reloadIfLinksExpired } from '../utils/expiredLinks';

/**
 * Card-level video player for hover-preview cards.
 * Uses native <video> so it fits into the existing card grid layout.
 * Declarative: playing/playbackRate/volume/muted are driven by props via useEffect.
 * Exposes getVideoElement() via ref for PiP support.
 */
const CardVideoPlayer = forwardRef(function CardVideoPlayer({
  url,
  poster,
  playing = false,
  controls = false,
  playbackRate = 1,
  volume = 1,
  muted = false,
  className,
  style,
  onVolumeChange,
  onPlay,
  onContextMenu,
  onTimeUpdate,
  onLoadedMetadata,
}, ref) {
  const videoRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getVideoElement: () => videoRef.current,
  }));

  // Play / pause
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (playing) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [playing]);

  // Playback rate
  useEffect(() => {
    const el = videoRef.current;
    if (el) el.playbackRate = playbackRate;
  }, [playbackRate]);

  // Volume / mute
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.volume = typeof volume === 'number' && !isNaN(volume) ? Math.min(Math.max(volume, 0), 1) : 1;
    el.muted = muted;
  }, [volume, muted]);

  return (
    <>
    <video
      ref={videoRef}
      src={url}
      poster={poster}
      controls={controls}
      controlsList="nodownload noremoteplayback"
      preload="none"
      className={['media-no-save', className].filter(Boolean).join(' ')}
      style={style}
      onContextMenu={onContextMenu}
      onVolumeChange={onVolumeChange}
      onPlay={onPlay}
      onTimeUpdate={onTimeUpdate}
      onLoadedMetadata={onLoadedMetadata}
      onError={() => reloadIfLinksExpired(url)}
    />
    {/* A failed poster raises no <video> error, so an expired thumbnail would just show
        black. This hidden probe loads the same (cached) link and reports its failure. */}
    {poster && (
      <img data-poster-probe src={poster} alt="" aria-hidden="true" style={{ display: 'none' }}
        onError={() => reloadIfLinksExpired(poster)} />
    )}
    </>
  );
});

export default React.memo(CardVideoPlayer);
